import { useState, useEffect } from 'react';
import { TextField, Typography, Container, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Link, Box, IconButton } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Settings } from '../types';
import { defaultSettings } from '../constants/settings';

export default function SettingsPage() {
  const [settings, setSettings] = useLocalStorage<Settings>('settings', defaultSettings);
  const [formData, setFormData] = useState<Settings>(settings);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const hasChanged = Object.keys(settings).some(key => 
      settings[key as keyof Settings] !== formData[key as keyof Settings]
    );
    setHasChanges(hasChanged);
  }, [formData, settings]);

  const handleInputChange = (field: keyof Settings) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: keyof Settings) => (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = () => {
    setSettings(formData);
    setHasChanges(false);
  };

  return (
    <Container maxWidth="sm" sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        pt: 2
      }}>
        <Typography variant="h5">Settings</Typography>
        <IconButton
          color={hasChanges ? "primary" : "default"}
          onClick={handleSave}
          title={hasChanges ? "Save changes" : "No changes to save"}
          sx={{
            transition: 'all 0.2s',
            transform: hasChanges ? 'scale(1.1)' : 'scale(1)',
            '&:hover': {
              transform: hasChanges ? 'scale(1.2)' : 'scale(1.1)'
            }
          }}
        >
          <SaveIcon />
        </IconButton>
      </Box>

      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        pr: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#555',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#666',
        }
      }}>
        <FormControl fullWidth margin="normal">
          <InputLabel>OpenAI URL</InputLabel>
          <Select
            value={formData.openaiUrl}
            label="OpenAI URL"
            onChange={handleSelectChange('openaiUrl')}
          >
            <MenuItem value="https://api.openai.com/v1">OpenAI</MenuItem>
            <MenuItem value="https://openrouter.ai/api/v1">OpenRouter AI</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          label="OpenAI API Key"
          value={formData.openaiKey}
          onChange={handleInputChange('openaiKey')}
          margin="normal"
          type="password"
          helperText={
            <Typography variant="caption">
              Get your API key from{' '}
              <Link 
                href={formData.openaiUrl === 'https://api.openai.com/v1' ? 'https://platform.openai.com/api-keys' : 'https://openrouter.ai/keys'}
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(
                    formData.openaiUrl === 'https://api.openai.com/v1' 
                      ? 'https://platform.openai.com/api-keys'
                      : 'https://openrouter.ai/keys',
                    '_blank'
                  );
                }}
              >
                {formData.openaiUrl === 'https://api.openai.com/v1' ? 'OpenAI' : 'OpenRouter AI'}
              </Link>
            </Typography>
          }
        />
        
        <TextField
          fullWidth
          label="Image to Text Model"
          value={formData.imageToTextModel}
          onChange={handleInputChange('imageToTextModel')}
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Text Model"
          value={formData.textModel}
          onChange={handleInputChange('textModel')}
          margin="normal"
        />

        <TextField
          fullWidth
          label="Image Router API Key"
          value={formData.imageRouterKey}
          onChange={handleInputChange('imageRouterKey')}
          margin="normal"
          type="password"
          helperText={
            <Typography variant="caption">
              Get your API key from{' '}
              <Link 
                href="https://ir-docs.myqa.cc" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('https://ir-docs.myqa.cc', '_blank');
                }}
              >
                Image Router
              </Link>
            </Typography>
          }
        />

        <TextField
          fullWidth
          label="Text to Image Model"
          value={formData.textToImageModel}
          onChange={handleInputChange('textToImageModel')}
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Image Generation System Prompt"
          value={formData.systemPrompt}
          onChange={handleInputChange('systemPrompt')}
          margin="normal"
          multiline
          rows={4}
        />
      </Box>
    </Container>
  );
} 